import type { ListPageConfig, FilterFieldConfig } from './types'

const RESERVED = new Set(['page', 'sort', 'order', 'tab', 'size'])

/**
 * Generates a TanStack Router `validateSearch` function from a ListPageConfig.
 *
 * Sanitises each filter param on navigation: unknown values → undefined (which
 * TanStack Router drops, falling back to defaultValue in the hook).
 *
 * Throws TypeError in development if any field's urlKey collides with a reserved
 * param name (q, page, sort, order, tab).
 */
export function generateValidateSearch(
  config: ListPageConfig,
): (search: Record<string, unknown>) => Record<string, unknown> {
  if (import.meta.env.DEV) {
    for (const field of config.fields) {
      const key = field.urlKey ?? field.id
      if (RESERVED.has(key)) {
        throw new TypeError(
          `[generateValidateSearch] Field "${field.id}" uses reserved URL param "${key}". ` +
          `Set a different urlKey. Reserved: ${[...RESERVED].join(', ')}`,
        )
      }
    }
  }

  return (raw: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {}

    // Pass reserved pagination/sort/tab params through (sanitised by the hook)
    if (typeof raw.page === 'number' && raw.page > 1) out.page = raw.page
    if (typeof raw.sort === 'string' && raw.sort) out.sort = raw.sort
    if (raw.order === 'asc' || raw.order === 'desc') out.order = raw.order
    // Rows-per-page, only as one of the configured choices — a free number here would let a URL ask
    // for any page size at all.
    const size = typeof raw.size === 'string' ? Number(raw.size) : raw.size
    if (typeof size === 'number' && config.pagination?.pageSizeOptions?.includes(size)) out.size = size

    // The tab param is whatever the config named it — `tab` is only the default. Reading the
    // literal 'tab' here would have silently dropped a page's tab on every navigation.
    const tabKey = config.tabs?.urlKey ?? 'tab'
    const rawTab = raw[tabKey]
    if (typeof rawTab === 'string' && rawTab) {
      const known = config.tabs?.options
      // The default tab is omitted from the URL, so it is not a valid param value either.
      if (!known || (rawTab !== config.tabs?.default && known.some((t) => t.key === rawTab))) {
        out[tabKey] = rawTab
      }
    }

    for (const field of config.fields) {
      const key = field.urlKey ?? field.id
      const raw_val = raw[key]
      out[key] = sanitiseField(field, raw_val)
    }

    return out
  }
}

function sanitiseField(field: FilterFieldConfig, raw: unknown): unknown {
  switch (field.type) {
    case 'search':
      return typeof raw === 'string' && raw ? raw : undefined

    case 'select': {
      if (typeof raw !== 'string' || !raw) return undefined
      const valid = field.options?.map((o) => o.value) ?? []
      return valid.length === 0 || valid.includes(raw) ? raw : undefined
    }

    case 'multi-select': {
      const arr = parseCommaSeparated(raw)
      if (!arr.length) return undefined
      // Filter to known options when options are defined
      if (field.options?.length) {
        const valid = new Set(field.options.map((o) => o.value))
        const filtered = arr.filter((v) => valid.has(v))
        return filtered.length ? filtered.join(',') : undefined
      }
      return arr.join(',')
    }

    case 'async-select': {
      // Accept any non-empty string IDs — server validates them
      const arr = parseCommaSeparated(raw)
      return arr.length ? arr.join(',') : undefined
    }

    case 'toggle':
      return raw === true || raw === 'true' ? true : undefined

    case 'tri-toggle': {
      // Unlike every other field here, an out-of-range value must NOT collapse to undefined
      // blindly: `false` and an explicit `null` are both selections. Only a genuinely
      // unrecognised value falls back to "param absent", which the hook reads as the default.
      if (raw === true || raw === 'true') return true
      if (raw === false || raw === 'false') return false
      if (raw === null || raw === 'null' || raw === 'all') return null
      return undefined
    }

    case 'date':
      // yyyy-mm-dd only. A free-form string here would reach the server as a date literal.
      return typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : undefined

    default:
      return undefined
  }
}

function parseCommaSeparated(raw: unknown): string[] {
  if (typeof raw === 'string') return raw.split(',').map((s) => s.trim()).filter(Boolean)
  if (Array.isArray(raw)) return (raw as unknown[]).map(String).filter(Boolean)
  return []
}
